import { getSlugFromURL } from "."

/**
 * Replaces `${key}` placeholders in a template string with corresponding values
 * from the provided data object. Supports dot-notation keys (e.g. `${user.name}`).
 * Returns "-" for any key that resolves to undefined or null.
 *
 * @param {string} template - The template string containing `${...}` placeholders.
 * @param {Object} [data={}] - The data object used to resolve placeholder values.
 * @returns {string} The template string with all placeholders replaced.
 */
export const parseTemplate = (template, data = {}) => {
    return template.replace(/\$\{(.*?)\}/g, (_, key) => {
        const keys = key.trim().split(".")
        let value = data

        for (let k of keys) value = value?.[k]

        return value ?? "-"
    })
}

/**
 * Advanced template renderer that supports BEGIN/END block loops and nested
 * template trees. Processes `<!-- BEGIN: blockname -->...<!-- END: blockname -->`
 * sections by iterating over matching arrays in the data object, then replaces
 * `${key}` placeholders with values from each item.
 *
 * Nested templates: Pass an array of template strings (innermost first) to
 * render a template tree. Each level is rendered and injected into the next
 * level's output via the data object.
 *
 * @param {string|string[]} template - A single template string, or an array of
 *   template strings ordered innermost-first (e.g. [card.tpl, main.tpl]).
 *   When an array is provided, each template's rendered output is passed to
 *   the next template as `${_childContent}` (or via explicit key in data).
 * @param {Object} data - The data object. Keys matching block names should be
 *   arrays of objects. Example: { patient: [{name:"A"}, {name:"B"}] }
 * @returns {string} The fully rendered HTML string.
 *
 * @example
 * // Single template with block loop:
 * const tpl = `
 *   <div>
 *     <!-- BEGIN: patient -->
 *     <span>${name}</span>
 *     <!-- END: patient -->
 *   </div>
 * `
 * renderTemplate(tpl, { patient: [{name: "Alice"}, {name: "Bob"}] })
 *
 * @example
 * // Nested template tree:
 * const cardTpl = '<div class="card">${name}</div>'
 * const mainTpl = '<div class="wrapper">${_childContent}</div>'
 * renderTemplate([cardTpl, mainTpl], { patient: [{name: "Alice"}] })
 */
export const renderTemplate = (template, data = {}) => {
    if (Array.isArray(template)) {
        return renderTemplateTree(template, data)
    }
    return renderSingleTemplate(template, data)
}

/**
 * Renders a single template string by first processing BEGIN/END blocks
 * (looping over arrays), then replacing remaining ${key} placeholders
 * with top-level data values.
 *
 * @param {string} template - The template string to render.
 * @param {Object} data - Data object with arrays for blocks and scalars for placeholders.
 * @returns {string} Rendered HTML string.
 */
function renderSingleTemplate(template, data) {
    const blockPattern = /<!-- BEGIN: (\w+) -->([\s\S]*?)<!-- END: \1 -->/g

    let result = template.replace(blockPattern, (_, blockName, blockContent) => {
        const items = data[blockName]

        if (!Array.isArray(items)) {
            return ""
        }

        return items
            .map((item, index) => {
                const itemData = { ...item, _index: index, _count: items.length }
                let rendered = renderSingleTemplate(blockContent, itemData)
                rendered = replacePlaceholders(rendered, itemData)
                return rendered
            })
            .join("")
    })

    result = replacePlaceholders(result, data)
    return result
}

/**
 * Replaces ${key} placeholders in a string with values from the data object.
 * Supports dot-notation keys. Returns empty string for undefined values
 * (unlike parseTemplate which returns "-").
 *
 * @param {string} str - String containing ${...} placeholders.
 * @param {Object} data - Data object to resolve values from.
 * @returns {string} String with placeholders replaced.
 */
function replacePlaceholders(str, data) {
    return str.replace(/\$\{(.*?)\}/g, (match, key) => {
        const keys = key.trim().split(".")
        let value = data

        for (const k of keys) {
            value = value?.[k]
        }

        if (value === undefined || value === null) {
            return match
        }
        return value
    })
}

/**
 * Renders a tree of nested templates from innermost to outermost. Each
 * template's rendered output is passed to the next template as
 * `_childContent` in the data object.
 *
 * @param {string[]} templates - Array of template strings ordered innermost-first.
 * @param {Object} data - Data object shared across all template levels.
 * @returns {string} The final rendered output from the outermost template.
 */
function renderTemplateTree(templates, data) {
    let childContent = ""

    for (let i = 0; i < templates.length; i++) {
        const tpl = templates[i]
        const levelData = { ...data, _childContent: childContent }
        childContent = renderSingleTemplate(tpl, levelData)
    }

    return childContent
}

/**
 * Fetches a `.tpl` template file for the given component (extension) from the
 * company-scoped media storage. Falls back to the slug derived from the current
 * URL when `componentName` is not provided. A cache-busting timestamp query
 * parameter is appended to every request.
 *
 * @param {string} [componentName] - The extension slug whose template should be
 *   loaded. If omitted, the slug is inferred from the current URL.
 * @returns {Promise<string>} The raw template string.
 * @throws {Error} If the HTTP response is not OK or the fetch itself fails.
 */
export async function loadtplTemplate(componentName) {
    try {
        const baseUrl = process.env.REACT_APP_API_URL || ""
        const companyKey = sessionStorage.getItem("app_key")
        const slug = componentName ? componentName : getSlugFromURL()

        const fileURL = `${baseUrl}static/media-files/asset/${companyKey}/dynamic-action/extensions/${slug}.tpl`

        const requestURL = `${fileURL}?v=${Date.now()}`
        const res = await fetch(requestURL)
        if (!res.ok) {
            throw new Error(
                `Template load failed for slug "${slug}" from "${requestURL}" (${res.status} ${res.statusText})`
            )
        }
        const template = await res.text()
        return template
    } catch (err) {
        console.error("Template load failed:", err)
        throw err
    }
}
